import { DocumentPlusIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/24/solid"
import React, { MouseEventHandler, ReactEventHandler, useContext } from "react"
import { FileUploader } from "react-drag-drop-files"
import { SiteProps } from "../../interfaces"
import Spinner from "../ui/Spinner"
import { AdminContext } from "~/contexts/AdminContext"
import { useSitePhotos } from "~/hooks/useSitePhotos"

const fileTypes = ["JPG", "JPEG", "PNG"]

export const Image = ({
  src,
  handleRemove,
  isLoading,
  onLoaded,
  onError,
}: {
  src: string
  handleRemove: MouseEventHandler<HTMLButtonElement>
  isLoading: boolean
  onLoaded: ReactEventHandler<HTMLImageElement>
  onError: ReactEventHandler<HTMLImageElement>
}) => (
  <div className="block flex-none bg-white w-24 h-24 relative overflow-hidden rounded-lg">
    {isLoading && (
      <div className="absolute w-full h-full top-0 left-0 bg-black bg-opacity-50 flex justify-center items-center">
        <Spinner size={8} className="w-8 h-8 text-white mr-0" />
      </div>
    )}
    {!isLoading && (
      <>
        <button
          type="button"
          className="group flex justify-center items-center z-10 w-7 h-7 p-1 absolute top-0 right-0 border-1.5 border-gray-300 hover:border-gray-400 rounded-full bg-gray-50 hover:bg-gray-100"
          onClick={handleRemove}
        >
          <XMarkIcon className="pointer-events-none stroke-gray-300 group-hover:stroke-gray-400 stroke-[1.5]" />
        </button>
        <img
          src={src}
          className="absolute w-full h-full object-cover object-center hover:opacity-90 cursor-pointer"
          alt="Uploaded content"
          onLoad={onLoaded}
          onError={onError}
          onClick={() => window.open(src, "_blank")}
        />
      </>
    )}
  </div>
)

function SitePhotoUpload({ site }: { site: SiteProps }) {
  const { showNotification } = useContext(AdminContext)

  const { imageStates, isLoading, handleAdd, handleRemove, updateImageLoadState } = useSitePhotos(
    site,
    showNotification,
  )

  if (!site) return null

  return (
    <>
      <label className="block text-md text-gray-900 mb-1">Site photos</label>
      <div className="gap-2 mb-2 mt-1 flex overflow-x-auto">
        {imageStates?.map(({ url, key }) => (
          <Image
            key={key}
            src={url}
            handleRemove={() => handleRemove(key)}
            isLoading={isLoading}
            onLoaded={() => updateImageLoadState(key, false)}
            onError={() => updateImageLoadState(key, false)}
          />
        ))}
      </div>

      <FileUploader
        handleChange={handleAdd}
        name="file"
        types={fileTypes}
        multiple
        children={
          <div className="border-2 border-dashed border-gray-300 p-8 h-24 flex items-center rounded-md cursor-pointer hover:bg-gray-75">
            <DocumentPlusIcon className="w-8 h-8 text-gray-500 mr-4" />
            <div className="flex justify-between w-full">
              <span className="text-gray-600 text-md">Click or drag a photo here to upload</span>
              <span className="text-gray-500 text-md">{fileTypes.join(", ")}</span>
            </div>
          </div>
        }
      />
    </>
  )
}

export default SitePhotoUpload
